/**
 * File upload configuration and supported file types.
 * Used for validation and multimodal content handling.
 */

/** Supported image MIME types */
export const SUPPORTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
] as const;

/** Supported PDF MIME type */
export const SUPPORTED_PDF_TYPE = "application/pdf" as const;

/** Supported audio MIME types */
export const SUPPORTED_AUDIO_TYPES = [
  "audio/webm",
  "video/webm", // WebM containers with audio-only content are often reported as video/webm
  "audio/mp4",
  "audio/mpeg",
  "audio/mp3", // Some browsers report mp3 as audio/mp3 instead of audio/mpeg
  "audio/ogg",
  "audio/wav",
  "audio/wave", // Alternate MIME type for WAV
  "audio/x-wav", // Alternate MIME type for WAV
  "audio/aac",
  "audio/x-m4a", // M4A audio files
  "audio/m4a", // M4A audio files (alternate)
  "audio/flac", // FLAC audio files
] as const;

/** All supported file types (images + PDF + audio) */
export const SUPPORTED_FILE_TYPES = [
  ...SUPPORTED_IMAGE_TYPES,
  SUPPORTED_PDF_TYPE,
  ...SUPPORTED_AUDIO_TYPES,
] as const;

/**
 * Type guard to check if a MIME type is a supported image
 */
export const isSupportedImageType = (
  mimeType: string,
): mimeType is (typeof SUPPORTED_IMAGE_TYPES)[number] => {
  return SUPPORTED_IMAGE_TYPES.includes(
    mimeType as (typeof SUPPORTED_IMAGE_TYPES)[number],
  );
};

/**
 * Type guard to check if a MIME type is supported
 */
export const isSupportedFileType = (
  mimeType: string,
): mimeType is (typeof SUPPORTED_FILE_TYPES)[number] => {
  return SUPPORTED_FILE_TYPES.includes(
    mimeType as (typeof SUPPORTED_FILE_TYPES)[number],
  );
};

/**
 * Type guard to check if a MIME type is a supported audio
 */
export const isSupportedAudioType = (
  mimeType: string,
): mimeType is (typeof SUPPORTED_AUDIO_TYPES)[number] => {
  return SUPPORTED_AUDIO_TYPES.includes(
    mimeType as (typeof SUPPORTED_AUDIO_TYPES)[number],
  );
};


/**
 * File size limits based on Anthropic API constraints.
 * All sizes in bytes.
 *
 * @see https://docs.anthropic.com/en/docs/vision
 * @see https://docs.anthropic.com/en/docs/build-with-claude/pdf-support
 */
export const FILE_SIZE_LIMITS = {
  /** Maximum image size: 5 MB (Anthropic API limit for base64 images) */
  IMAGE_MAX_SIZE: 5 * 1024 * 1024, // 5 MB

  /** Maximum audio file size: 100 MB */
  AUDIO_MAX_SIZE: 100 * 1024 * 1024, // 100 MB

  /**
   * Maximum raw PDF size: 24 MB
   * Note: Base64 encoding increases size by ~33%, so 24 MB raw → ~32 MB encoded
   * This ensures we stay under the 32 MB total request limit
   */
  PDF_RAW_MAX_SIZE: 24 * 1024 * 1024, // 24 MB

  /** Maximum PDF size after base64 encoding: 32 MB */
  PDF_ENCODED_MAX_SIZE: 32 * 1024 * 1024, // 32 MB

  /** Maximum total request size (all content combined): 32 MB */
  TOTAL_REQUEST_SIZE: 32 * 1024 * 1024, // 32 MB

  /** Maximum image dimension (width or height): 8000 pixels */
  IMAGE_MAX_DIMENSION: 8000, // 8000×8000 px

  /** Optimal image megapixels for best quality/cost ratio: 1.15 MP */
  IMAGE_OPTIMAL_MEGAPIXELS: 1.15e6, // ~1092×1092 px

  /** Recommended maximum edge length for single images: 1568 pixels */
  IMAGE_RECOMMENDED_EDGE: 1568,

  /** Maximum edge length for multi-image requests (>20 images): 2000 pixels */
  IMAGE_MULTI_REQUEST_EDGE: 2000,

  /** Maximum number of images per request: 100 */
  MAX_IMAGES_PER_REQUEST: 100,

  /** Maximum PDF page count: 100 pages */
  MAX_PDF_PAGES: 100,

  // Future: Files API limits (not implemented yet)
  /** Files API max file size: 500 MB (future enhancement) */
  FILES_API_MAX_SIZE: 500 * 1024 * 1024, // 500 MB

  /** Files API organization storage limit: 100 GB (future enhancement) */
  FILES_API_ORG_STORAGE: 100e9, // 100 GB
} as const;

/**
 * Validation result type
 */
export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Format bytes to human-readable string
 * @param bytes - Number of bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "5.23 MB")
 */
export function formatFileSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Validate image file size and format
 * @param file - File to validate
 * @returns Validation result with error message if invalid
 */
function validateImageFile(file: File): ValidationResult {
  // Format check
  if (!isSupportedImageType(file.type)) {
    return {
      valid: false,
      error: `Unsupported image format: ${file.type}`,
    };
  }

  // Size check
  if (file.size > FILE_SIZE_LIMITS.IMAGE_MAX_SIZE) {
    return {
      valid: false,
      error: `Image exceeds 5 MB limit (${formatFileSize(file.size)})`,
    };
  }

  return { valid: true };
}

/**
 * Validate audio file size and format
 * @param file - File to validate
 * @returns Validation result with error message if invalid
 */
export function validateAudioFile(file: File): ValidationResult {
  // Format check
  if (!isSupportedAudioType(file.type)) {
    return {
      valid: false,
      error: `Unsupported audio format: ${file.type}`,
    };
  }

  // Size check
  if (file.size > FILE_SIZE_LIMITS.AUDIO_MAX_SIZE) {
    return {
      valid: false,
      error: `Audio exceeds 100 MB limit (${formatFileSize(file.size)})`,
    };
  }

  return { valid: true };
}

/**
 * Validate PDF file size and format
 * @param file - File to validate
 * @returns Validation result with error message if invalid
 */
function validatePDFFile(file: File): ValidationResult {
  // Format check
  if (file.type !== SUPPORTED_PDF_TYPE) {
    return {
      valid: false,
      error: `Unsupported PDF format: ${file.type}`,
    };
  }

  // Size check (raw file, will be ~33% larger after base64)
  if (file.size > FILE_SIZE_LIMITS.PDF_RAW_MAX_SIZE) {
    return {
      valid: false,
      error: `PDF exceeds 24 MB limit (${formatFileSize(file.size)}). After encoding, this would exceed the 32 MB API limit.`,
    };
  }

  return { valid: true };
}

/**
 * Validate image dimensions
 * @param file - Image file to validate
 * @returns Promise with validation result
 */
export async function validateImageDimensions(
  file: File,
): Promise<ValidationResult> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      if (
        img.width > FILE_SIZE_LIMITS.IMAGE_MAX_DIMENSION ||
        img.height > FILE_SIZE_LIMITS.IMAGE_MAX_DIMENSION
      ) {
        resolve({
          valid: false,
          error: `Image dimensions ${img.width}×${img.height} exceed 8000×8000 px limit`,
        });
      } else {
        resolve({ valid: true });
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ valid: false, error: "Failed to load image for validation" });
    };

    img.src = url;
  });
}

/**
 * Validate a file based on its type
 * @param file - File to validate
 * @returns Validation result with error message if invalid
 */
export function validateFile(file: File): ValidationResult {
  if (isSupportedImageType(file.type)) {
    return validateImageFile(file);
  } else if (file.type === SUPPORTED_PDF_TYPE) {
    return validatePDFFile(file);
  } else if (isSupportedAudioType(file.type)) {
    return validateAudioFile(file);
  } else {
    return {
      valid: false,
      error: `Unsupported file type: ${file.type}`,
    };
  }
}
